import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import DashboardLayout from '../../components/DashboardLayout';
import RecordPage, { FormSection } from '../../components/shell/RecordPage';
import { CopyField } from '../../components/shell/PasswordField';
import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';
import { Switch } from '../../components/ui/switch';
import { Globe, Link as LinkIcon, Users, ShieldCheck, Check, Copy, FloppyDisk, ArrowSquareOut, LinkSimple, Devices } from '@phosphor-icons/react';
import { toast } from 'sonner';
import CustomDomainDialog from '../../components/CustomDomainDialog';

const SESSION_DEFAULTS = {
  enabled: false,
  away_enabled: true,
  away_seconds: 10,
  idle_enabled: false,
  idle_seconds: 300,
  idle_warn_seconds: 30,
  scope: 'learning',
  count_attempt: true,
};

// How many devices one account may be signed in on at once, per role. 0 = no limit.
// Must match utils/sessions.py, which clamps whatever arrives anyway.
const LIMIT_DEFAULTS = {
  enabled: false,
  limits: { learner: 1, parent: 2, instructor: 3, tenant_admin: 3 },
};

const LIMIT_ROLES = [
  { key: 'learner', label: 'Students', hint: 'One device is the usual choice — it is what stops a login being passed around a class.' },
  { key: 'parent', label: 'Parents', hint: 'Two covers a phone and a laptop, or two parents sharing one login.' },
  { key: 'instructor', label: 'Teachers', hint: 'Staff move between a classroom machine, a laptop and a phone.' },
  { key: 'tenant_admin', label: 'Organisation admins', hint: 'Keep this generous — locking your own admins out is an awkward morning.' },
];

export default function TenantSettings() {
  const { api } = useAuth();
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [copiedField, setCopiedField] = useState(null);
  const [domainDialogOpen, setDomainDialogOpen] = useState(false);

  const [settings, setSettings] = useState({
    allow_learner_registration: true,
    allow_instructor_registration: false
  });
  const [session, setSession] = useState(SESSION_DEFAULTS);
  const patchSession = (p) => { setSession((s) => ({ ...s, ...p })); setDirty(true); };
  const [limits, setLimits] = useState(LIMIT_DEFAULTS);
  const patchLimits = (p) => { setLimits((s) => ({ ...s, ...p })); setDirty(true); };
  const patchLimit = (role, value) => {
    setLimits((s) => ({ ...s, limits: { ...s.limits, [role]: value } }));
    setDirty(true);
  };

  useEffect(() => {
    fetchTenantSettings();
  }, []);

  const fetchTenantSettings = async () => {
    try {
      // Get current tenant info via /tenant/me endpoint
      const response = await api.get('/tenant/me');
      setTenant(response.data);
      setSettings({
        allow_learner_registration: response.data.settings?.allow_learner_registration !== false,
        allow_instructor_registration: response.data.settings?.allow_instructor_registration === true
      });
      setSession({ ...SESSION_DEFAULTS, ...(response.data.settings?.session_security || {}) });
      const stored = response.data.settings?.session_limits || {};
      setLimits({
        ...LIMIT_DEFAULTS, ...stored,
        limits: { ...LIMIT_DEFAULTS.limits, ...(stored.limits || {}) },
      });
      setDirty(false);
    } catch (error) {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const updateSetting = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
    setDirty(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Backend $sets the whole settings object — merge over existing keys so nothing else is lost
      await api.put('/tenant/settings', {
        ...(tenant?.settings || {}), ...settings, session_security: session, session_limits: limits,
      });
      toast.success('Organization settings saved');
      await fetchTenantSettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const copyToClipboard = async (text, field) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
      toast.success('Copied to clipboard');
    } catch {
      toast.error('Failed to copy');
    }
  };

  const CopyButton = ({ text, field }) => (
    <button
      type="button"
      onClick={() => copyToClipboard(text, field)}
      className="p-1.5 rounded hover:bg-muted transition-colors"
      title="Copy to clipboard"
    >
      {copiedField === field ? (
        <Check className="w-4 h-4 text-green-600" />
      ) : (
        <Copy className="w-4 h-4 text-muted-foreground" />
      )}
    </button>
  );

  if (loading) {
    return (
      <DashboardLayout>
        <RecordPage title="Organization settings" loading onBack={null} data-testid="tenant-settings-page" />
      </DashboardLayout>
    );
  }

  const fullPortalUrl = tenant ? `${window.location.origin}/tenant/${tenant.subdomain}` : '';
  const tenantHost = tenant ? (tenant.host || `${tenant.subdomain}.${tenant.base_domain || 'mylms.in'}`) : '';
  const subdomainUrl = tenant ? `https://${tenantHost}` : '';
  const preferredUrl = tenant?.custom_domain ? `https://${tenant.custom_domain}` : subdomainUrl;
  // The public lead-capture form only exists under the path-based tenant route
  const enquireUrl = tenant ? `${window.location.origin}/tenant/${tenant.subdomain}/enquire` : '';

  /* ──────────────────────────────────────────────────────────────────────────
   * The page is grouped into tabs. It had grown into one long scroll of unrelated
   * things — portal URLs, who may register, how sessions behave, links to share —
   * and the setting you wanted was always somewhere below the fold. Every panel
   * stays mounted, so an edit on one tab survives a trip to another and the one
   * Save at the top writes all of them.
   * ──────────────────────────────────────────────────────────────────────── */
  const portalTab = (
    <>
      {/* Portal Information */}
      <FormSection title="Portal Information" icon={Globe} description="Your organization's portal URLs and identifiers" bodyClassName="p-3 space-y-3">
        <div className="form-grid">
          <div className="field">
            <Label>Organization Name</Label>
            <div className="h-8 flex items-center rounded border border-input bg-muted/40 px-2.5 text-[13px] font-medium truncate">{tenant?.name}</div>
          </div>
          <CopyField label="Subdomain" value={tenant?.subdomain || ''} testid="subdomain-field" />
          <CopyField label="Portal URL (share this one)" value={`${fullPortalUrl}/login`} testid="path-url-field" className="sm:col-span-2" />
          <CopyField label="Subdomain URL (needs wildcard DNS)" value={`${subdomainUrl}/login`} testid="sub-url-field" className="sm:col-span-2" />
        </div>
        <p className="field-hint">
          Give your instructors and learners the portal URL — it works straight away, on any platform host.
          The subdomain address only resolves once wildcard DNS for {tenant?.base_domain || 'the base domain'} points at the platform;
          for a branded address, set up a custom domain below instead.
        </p>
      </FormSection>

      {/* Custom Domain */}
      <FormSection
        title="Custom Domain"
        icon={LinkIcon}
        description="Use your own domain like learn.yourcompany.com — managed automatically via the Railway API. Use “Custom domain” in the toolbar to add or manage it."
        bodyClassName="p-3"
      >
        {tenant?.custom_domain ? (
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 flex items-center gap-2 min-w-0" data-testid="custom-domain-active">
            <Check className="w-4 h-4 text-emerald-600 flex-shrink-0" />
            <div className="min-w-0">
              <p className="field-hint">Active domain</p>
              <p className="font-mono text-sm truncate">https://{tenant.custom_domain}</p>
            </div>
            <CopyButton text={`https://${tenant.custom_domain}`} field="custom-domain" />
          </div>
        ) : (
          <div className="border border-dashed rounded-lg p-4 text-center space-y-1" data-testid="custom-domain-empty">
            <p className="text-sm text-muted-foreground">No custom domain set</p>
            <p className="field-hint">Add your own branded domain. We'll auto-register it with Railway.</p>
          </div>
        )}
      </FormSection>
    </>
  );

  const registrationTab = (
    <>
      {/* Registration Settings */}
      <FormSection title="Registration Settings" icon={Users} description="Control who can register on your portal" bodyClassName="p-3 space-y-2">
        <div className="flex items-center justify-between gap-4 rounded border border-border px-3 py-2">
          <div className="space-y-0.5">
            <Label htmlFor="allow_learner_registration" className="text-[13px] text-foreground">Allow Learner Self-Registration</Label>
            <p className="field-hint">
              Let learners create their own accounts on your portal
            </p>
          </div>
          <Switch
            id="allow_learner_registration"
            checked={settings.allow_learner_registration}
            onCheckedChange={(checked) => updateSetting('allow_learner_registration', checked)}
            data-testid="learner-registration-switch"
          />
        </div>

        <div className="flex items-center justify-between gap-4 rounded border border-border px-3 py-2">
          <div className="space-y-0.5">
            <Label htmlFor="allow_instructor_registration" className="text-[13px] text-foreground">Allow Instructor Self-Registration</Label>
            <p className="field-hint">
              Let instructors request accounts (requires admin approval)
            </p>
          </div>
          <Switch
            id="allow_instructor_registration"
            checked={settings.allow_instructor_registration}
            onCheckedChange={(checked) => updateSetting('allow_instructor_registration', checked)}
            data-testid="instructor-registration-switch"
          />
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded px-3 py-2">
          <p className="text-xs text-blue-800 flex items-center gap-2">
            <ShieldCheck className="w-4 h-4" />
            Instructors can also be invited directly from the Instructor Management page
          </p>
        </div>
      </FormSection>

      {/* Quick Links */}
    </>
  );

  const securityTab = (
    <>
      {/* ------------------------------------------------- leaving the screen */}
      <FormSection
        title="Signing students out when they leave the screen"
        icon={ShieldCheck}
        description="Applies to students only. Teachers, parents and admins are never signed out this way."
        actions={<Switch checked={session.enabled} onCheckedChange={(v) => patchSession({ enabled: v })} data-testid="session-security-switch" />}
        bodyClassName={session.enabled ? 'p-3 space-y-4' : 'p-0'}
      >
        {session.enabled && (
          <>
            <div className="field">
              <Label htmlFor="ss-scope">Where it applies</Label>
              <select id="ss-scope" className="form-select" value={session.scope}
                onChange={(e) => patchSession({ scope: e.target.value })} data-testid="session-scope">
                <option value="assessments">Quizzes and exams only</option>
                <option value="learning">Quizzes, exams and course reading</option>
                <option value="everywhere">Everywhere a student goes</option>
              </select>
              <p className="field-hint">Dashboards and the diary are left alone unless you pick “everywhere”.</p>
            </div>

            <div className="rounded-md border border-border p-3 space-y-2">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-[13px] font-medium" data-find="Switching tab or app">Switching tab or app</p>
                  <p className="field-hint">The strict one. There is no warning — a hidden tab cannot show one — so the student is told why on the sign-in page.</p>
                </div>
                <Switch checked={session.away_enabled} onCheckedChange={(v) => patchSession({ away_enabled: v })} data-testid="away-switch" />
              </div>
              {session.away_enabled && (
                <div className="field">
                  <Label htmlFor="ss-away">Seconds away before signing out</Label>
                  <Input id="ss-away" type="number" min="3" max="3600" className="w-[120px]"
                    value={session.away_seconds}
                    onChange={(e) => patchSession({ away_seconds: parseInt(e.target.value, 10) || 10 })}
                    data-testid="away-seconds" />
                </div>
              )}
            </div>

            <div className="rounded-md border border-border p-3 space-y-2">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-[13px] font-medium" data-find="Sitting idle">Sitting idle on the page</p>
                  <p className="field-hint">
                    Nothing clicked, typed or scrolled while the page is in front of them. Keep this well above
                    a few seconds — reading a page or thinking about a question is not idleness. A countdown
                    warns first and any movement cancels it.
                  </p>
                </div>
                <Switch checked={session.idle_enabled} onCheckedChange={(v) => patchSession({ idle_enabled: v })} data-testid="idle-switch" />
              </div>
              {session.idle_enabled && (
                <div className="flex gap-3 flex-wrap">
                  <div className="field">
                    <Label htmlFor="ss-idle">Seconds idle before signing out</Label>
                    <Input id="ss-idle" type="number" min="10" max="86400" className="w-[120px]"
                      value={session.idle_seconds}
                      onChange={(e) => patchSession({ idle_seconds: parseInt(e.target.value, 10) || 300 })}
                      data-testid="idle-seconds" />
                  </div>
                  <div className="field">
                    <Label htmlFor="ss-warn">Warn this many seconds first</Label>
                    <Input id="ss-warn" type="number" min="5" max="600" className="w-[120px]"
                      value={session.idle_warn_seconds}
                      onChange={(e) => patchSession({ idle_warn_seconds: parseInt(e.target.value, 10) || 30 })}
                      data-testid="idle-warn-seconds" />
                  </div>
                </div>
              )}
            </div>

            <div className="flex items-start justify-between gap-4 rounded-md border border-border p-3">
              <div>
                <p className="text-[13px] font-medium" data-find="Count the attempt">Count the attempt when a quiz is interrupted</p>
                <p className="field-hint">
                  The quiz is handed in with whatever the student had answered and uses up one of their attempts,
                  marked as auto-submitted. Switch this off and an interrupted quiz costs them nothing.
                </p>
              </div>
              <Switch checked={session.count_attempt} onCheckedChange={(v) => patchSession({ count_attempt: v })} data-testid="count-attempt-switch" />
            </div>
          </>
        )}
      </FormSection>
      {/* ------------------------------------------------- how many devices at once */}
      <FormSection
        title="How many devices one account may use at once"
        icon={Devices}
        description="Signing in on a new device signs out the one that has gone longest without being used — nobody is ever locked out by a session they forgot to end. Changing a number here applies from the next sign-in; to clear someone's devices now, open their record under Users."
        actions={<Switch checked={limits.enabled} onCheckedChange={(v) => patchLimits({ enabled: v })} data-testid="session-limits-switch" />}
        bodyClassName={limits.enabled ? 'p-3 space-y-2' : 'p-0'}
      >
        {limits.enabled && (
          <>
            <div className="form-grid-2">
              {LIMIT_ROLES.map((role) => (
                <div className="field" key={role.key}>
                  <Label htmlFor={`limit-${role.key}`} data-find={`${role.label} devices`}>{role.label}</Label>
                  <Input
                    id={`limit-${role.key}`}
                    type="number"
                    min="0"
                    max="20"
                    className="w-[120px]"
                    value={limits.limits[role.key]}
                    onChange={(e) => patchLimit(role.key, Math.max(0, Math.min(20, parseInt(e.target.value, 10) || 0)))}
                    data-testid={`limit-${role.key}`}
                  />
                  <p className="field-hint">
                    {limits.limits[role.key] === 0 ? 'No limit. ' : ''}{role.hint}
                  </p>
                </div>
              ))}
            </div>
            <p className="field-hint">
              0 means no limit. A single person can be given their own number on their record
              under Users, which overrides the number for their role in both directions.
            </p>
          </>
        )}
      </FormSection>

    </>
  );

  const linksTab = (
    <>
      {/* ------------------------------------------------- quick links */}
      <FormSection title="Quick Links" icon={LinkSimple} description="Share these links with your team" bodyClassName="p-3">
        <div className="form-grid">
          <CopyField label="Learner login" value={`${preferredUrl}/login`} testid="learner-link" />
          <CopyField label="Instructor login" value={`${preferredUrl}/login`} testid="instructor-link" />
          {settings.allow_learner_registration && (
            <CopyField label="Learner registration" value={`${preferredUrl}/login`} testid="register-link" />
          )}
          <CopyField label="Public lead capture form" value={enquireUrl} testid="enquire-link" className="sm:col-span-2" />
        </div>
      </FormSection>
    </>
  );

  return (
    <DashboardLayout>
      <RecordPage
        data-testid="tenant-settings-page"
        title={tenant?.name || 'Organization settings'}
        subtitle={`Organization settings · ${tenantHost}`}
        onBack={null}
        onRefresh={fetchTenantSettings}
        refreshing={loading}
        dirty={dirty}
        primary={[
          { key: 'save', label: saving ? 'Saving…' : 'Save', icon: FloppyDisk, onClick: handleSave, disabled: saving, testid: 'save-tenant-settings-btn' },
        ]}
        secondary={[
          { key: 'domain', label: 'Custom domain', icon: LinkSimple, onClick: () => setDomainDialogOpen(true), testid: tenant?.custom_domain ? 'manage-custom-domain-btn' : 'add-custom-domain-btn' },
          { key: 'portal', label: 'Open portal', icon: ArrowSquareOut, onClick: () => window.open(`${fullPortalUrl}/login`, '_blank', 'noopener'), disabled: !tenant, testid: 'open-portal-btn' },
        ]}
        storageKey="tenant-settings"
        tabs={[
          { key: 'portal', label: 'Portal', icon: Globe, content: portalTab },
          { key: 'registration', label: 'Registration', icon: Users, content: registrationTab },
          { key: 'security', label: 'Security', icon: ShieldCheck, content: securityTab },
          { key: 'links', label: 'Links', icon: LinkSimple, content: linksTab },
        ]}
      />
      {/* Custom Domain dialog (Railway-managed) — outside the page, because a page with
          tabs renders its tabs' content rather than its children. */}
      <CustomDomainDialog
        open={domainDialogOpen}
        onOpenChange={setDomainDialogOpen}
        tenant={tenant ? { ...tenant, name: tenant.name } : null}
        onChange={fetchTenantSettings}
      />
    </DashboardLayout>
  );
}
